import { debounce } from 'vue-debounce';
export const TreeViewHelpers = {
  data() {
    return {
      scrollIntoViewOnUpdated: true,
      scrollIntoViewOnNewNodeAdded: true
    };
  },
  updated(this: any) {
    if (!this.scrollIntoViewOnUpdated) {
      return;
    }
    this.scrollSelectedNodeIntoView();
  },
  methods: {
    scrollSelectedNodeIntoView(this: any) {
      this.$nextTick(
        debounce(() => {
          const selectedNode = document.querySelector('.tree-node.selected');
          const rect = selectedNode?.getBoundingClientRect();
          if (selectedNode && rect) {
            // only scroll into view if not within the view already, to prevent the tree view moving around
            if (rect.top <= 0 || rect.bottom >= (window.innerHeight || document.documentElement.clientHeight)) {
              selectedNode.scrollIntoView(false);
            }
          }
        }, 250)
      );
    },
    selectNode(node: any) {
      node = node[0];
      if (node) {
        node.select();
        this.expandParentNode(node);
      }
    },
    expandNode(node: any) {
      node.expand();
    },
    unselectNode(node: any) {
      node = node[0];
      if (node) {
        node.unselect();
        this.collapseParentNode(node);
      }
    },
    expandParentNode(node: any) {
      const parentNode = node.parent;
      if (parentNode) {
        parentNode.expand();
        this.expandParentNode(parentNode);
      }
    },
    collapseParentNode(node: any) {
      const parentNode = node.parent;
      if (parentNode) {
        parentNode.collapse();
        this.collapseParentNode(parentNode);
      }
    },
    expandAll(this: any, treeName: string) {
      const treeElement = this.$refs[treeName];
      if (treeElement) {
        treeElement.expandAll();
      }
    },
    collapseAll(this: any, treeName: string) {
      const treeElement = this.$refs[treeName];
      if (treeElement) {
        treeElement.collapseAll();
      }
    },
    addNewNodesToTree(this: any, newNodes: any[], previousNodes: any[], parentNode: any = null, treeName = 'tree') {
      let newNodeAdded = false;
      for (const [index, newNode] of newNodes.entries()) {
        // first try to match on router data for the node, and fallback onto matching by text
        const existingNode =
          newNode.data.treeNodePath && newNode.data.routeName
            ? previousNodes.find(
                (n: any) =>
                  n.data.treeNodePath === newNode.data.treeNodePath && n.data.routeName === newNode.data.routeName
              )
            : previousNodes.find((n: any) => n.text === newNode.text);
        if (existingNode) {
          // update text if changed and check children
          if (newNode.text !== existingNode.text) {
            const treeNode = this.findMatchingNode(existingNode);
            treeNode.text = newNode.text;
          }
          if (newNode.children) {
            existingNode.children = existingNode.children || [];
            this.addNewNodesToTree(newNode.children, existingNode.children, existingNode, treeName);
          }
        } else {
          if (index === 0) {
            // if first item in current level then append to parent
            if (!parentNode) {
              this.$refs[treeName].prepend(newNode);
            } else {
              const parentTreeNode = this.findMatchingNode(parentNode, treeName);
              this.$refs[treeName].prepend(parentTreeNode, newNode);
            }
          } else {
            // later items can be added to after the node before
            const nodeBefore = previousNodes[index - 1];
            const treeNodeBefore = this.findMatchingNode(nodeBefore, treeName);
            this.$refs[treeName].after(treeNodeBefore, newNode);
          }
          newNodeAdded = true;
        }
      }
      // we don't trigger this if scrollIntoViewOnUpdated, as it will automatically scroll
      if (newNodeAdded && this.scrollIntoViewOnNewNodeAdded && !this.scrollIntoViewOnUpdated) {
        this.scrollSelectedNodeIntoView();
      }
    },
    removeDeletedNodesFromTree(this: any, newNodes: any[], previousNodes: any[], treeName = 'tree') {
      for (const previousNode of previousNodes) {
        const newNode = newNodes.find((n: any) => n.text === previousNode.text);
        if (newNode) {
          // does not need deleting, so check children
          if (previousNode.children) {
            newNode.children = newNode.children ?? [];
            this.removeDeletedNodesFromTree(newNode.children, previousNode.children, treeName);
          }
        } else {
          const nodeToDelete = this.findMatchingNode(previousNode, treeName);
          this.$refs[treeName].remove(nodeToDelete);
        }
      }
    },
    findMatchingNode(this: any, node: any, treeName = 'tree') {
      return this.$refs[treeName].find({
        data: { treeNodePath: node.data.treeNodePath, routeName: node.data.routeName },
        text: node.text
      })[0];
    }
  }
};
